/**
 * SYSTELOS MONITOR - Página de Clientes
 * Lista unificada com status DEC e DJE
 * 
 * @version 2.0.0
 * @date 24/01/2026
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Users,
  Plus,
  Search,
  RefreshCw,
  Eye,
  Edit,
  Building2,
  CheckCircle,
  AlertCircle,
  Scale,
} from 'lucide-react'
import { AppLayout } from '@/components/layout'
import { PageContainer, PageBanner, PageSection, StatCard } from '@/components/page'
import clientesService from '@/services/clientesService'
import type { Cliente, ClienteFiltros } from '@/types'
import toast from 'react-hot-toast'

import NovoClienteModal from './components/NovoClienteModal'

// ============================================
// COMPONENTES AUXILIARES
// ============================================

/**
 * Badge de status para DEC/DJE
 */
function StatusBadge({ 
  ativo, 
  ultimoErro, 
  novas = 0 
}: { 
  ativo: boolean | null
  ultimoErro?: string | null
  novas?: number
}) {
  // Inativo
  if (!ativo) {
    return (
      <span className="text-sm text-gray-400">--</span>
    )
  }

  // Erro na última consulta
  if (ultimoErro) {
    return (
      <span 
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700"
        title={ultimoErro}
      >
        <AlertCircle className="w-3 h-3" />
        Erro
      </span>
    )
  }

  // Ativo com novas comunicações
  if (novas > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
        {novas} {novas === 1 ? 'nova' : 'novas'}
      </span>
    )
  }

  // Ativo sem pendências
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">
      <CheckCircle className="w-3 h-3" />
      Ativo
    </span>
  )
}

/**
 * Formata data/hora da última consulta
 */
function formatarUltimaConsulta(dataDec?: string | null, dataDje?: string | null): string {
  const datas: Date[] = []
  
  if (dataDec) datas.push(new Date(dataDec))
  if (dataDje) datas.push(new Date(dataDje))
  
  if (datas.length === 0) return '--'
  
  const maisRecente = datas.sort((a, b) => b.getTime() - a.getTime())[0]
  
  return maisRecente.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export default function Clientes() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Estados
  const [loading, setLoading] = useState(true)
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [stats, setStats] = useState({ 
    total: 0, 
    ativos: 0, 
    inativos: 0,
    decAtivos: 0,
    djeAtivos: 0,
    comPendencias: 0
  })
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 })

  // Filtros
  const [busca, setBusca] = useState(searchParams.get('busca') || '')
  const [filtroAtivo, setFiltroAtivo] = useState<'todos' | 'true' | 'false'>(
    (searchParams.get('ativo') as 'todos' | 'true' | 'false') || 'todos'
  )
  const [filtroMonitoramento, setFiltroMonitoramento] = useState<'todos' | 'dec' | 'dje'>(
    (searchParams.get('monitoramento') as 'todos' | 'dec' | 'dje') || 'todos'
  )

  // Modal
  const [modalNovoAberto, setModalNovoAberto] = useState(false)

  // Carregar clientes
  const carregarClientes = async () => {
    setLoading(true)
    try {
      const filtros: ClienteFiltros = {
        page: pagination.page,
        limit: pagination.limit,
        busca: busca || undefined,
        ativo: filtroAtivo,
      }

      const [clientesRes, statsRes] = await Promise.all([
        clientesService.listar(filtros),
        clientesService.stats(),
      ])

      let clientesFiltrados = clientesRes.data

      // Filtro por tipo de monitoramento (frontend)
      if (filtroMonitoramento === 'dec') {
        clientesFiltrados = clientesFiltrados.filter((c: Cliente) => c.dec_ativo)
      } else if (filtroMonitoramento === 'dje') {
        clientesFiltrados = clientesFiltrados.filter((c: Cliente) => c.dje_ativo)
      }

      setClientes(clientesFiltrados)
      setPagination(clientesRes.pagination)
      
      if (statsRes.data) {
        setStats({
          total: statsRes.data.total || 0,
          ativos: statsRes.data.ativos || 0,
          inativos: statsRes.data.inativos || 0,
          decAtivos: statsRes.data.decAtivos || 0,
          djeAtivos: statsRes.data.djeAtivos || 0,
          comPendencias: statsRes.data.comPendencias || 0,
        })
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
      toast.error('Erro ao carregar clientes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    carregarClientes()
  }, [pagination.page, filtroAtivo, filtroMonitoramento])

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        carregarClientes()
      } else {
        setPagination((prev) => ({ ...prev, page: 1 }))
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [busca])

  // Formatar CNPJ
  const formatarCNPJ = (cnpj: string) => {
    if (!cnpj) return ''
    const numeros = cnpj.replace(/\D/g, '')
    if (numeros.length !== 14) return cnpj
    return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }

  // Handler de stat card click
  const handleStatClick = (filtro: string, valor: string) => {
    if (filtro === 'ativo') {
      setFiltroAtivo(valor as 'todos' | 'true' | 'false')
      setFiltroMonitoramento('todos')
      setSearchParams(valor === 'todos' ? {} : { ativo: valor })
    } else if (filtro === 'monitoramento') {
      setFiltroMonitoramento(valor as 'todos' | 'dec' | 'dje')
      setFiltroAtivo('todos')
    }
  }

  return (
    <AppLayout pagina="Clientes">
      <PageContainer>
        {/* Banner */}
        <PageBanner
          titulo="Clientes"
          subtitulo="Gerencie os clientes monitorados no DEC e DJE"
          icon={<Users className="w-6 h-6" />}
          acoes={
            <>
              <PageBanner.BotaoSecundario 
                icon={<RefreshCw className="w-4 h-4" />} 
                onClick={carregarClientes} 
                loading={loading}
              >
                Atualizar
              </PageBanner.BotaoSecundario>
              <PageBanner.BotaoPrimario 
                icon={<Plus className="w-4 h-4" />} 
                onClick={() => setModalNovoAberto(true)}
              >
                Novo Cliente
              </PageBanner.BotaoPrimario>
            </>
          }
        />

        {/* Estatísticas */}
        <StatCard.Grid colunas={{ mobile: 2, tablet: 3, desktop: 5 }}>
          <StatCard
            label="Total"
            valor={stats.total}
            icon={Users}
            cor="blue"
            onClick={() => handleStatClick('ativo', 'todos')}
            ativo={filtroAtivo === 'todos' && filtroMonitoramento === 'todos'}
          />
          <StatCard
            label="Ativos"
            valor={stats.ativos}
            icon={CheckCircle}
            cor="green"
            onClick={() => handleStatClick('ativo', 'true')}
            ativo={filtroAtivo === 'true'}
          />
          <StatCard
            label="DEC Ativos"
            valor={stats.decAtivos}
            icon={Building2}
            cor="teal"
            onClick={() => handleStatClick('monitoramento', 'dec')}
            ativo={filtroMonitoramento === 'dec'}
          />
          <StatCard
            label="DJE Ativos"
            valor={stats.djeAtivos}
            icon={Scale}
            cor="purple"
            onClick={() => handleStatClick('monitoramento', 'dje')}
            ativo={filtroMonitoramento === 'dje'}
          />
          <StatCard
            label="Com Pendências"
            valor={stats.comPendencias}
            icon={AlertCircle}
            cor="amber"
          />
        </StatCard.Grid>

        {/* Filtros */}
        <PageSection.Filter>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por razão social, CNPJ..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D] transition-all"
              />
            </div>
            <select
              value={filtroAtivo}
              onChange={(e) => handleStatClick('ativo', e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D]"
            >
              <option value="todos">Todos os Status</option>
              <option value="true">Apenas Ativos</option>
              <option value="false">Apenas Inativos</option>
            </select>
            <select
              value={filtroMonitoramento}
              onChange={(e) => setFiltroMonitoramento(e.target.value as 'todos' | 'dec' | 'dje')}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D]"
            >
              <option value="todos">Todos os Monitoramentos</option>
              <option value="dec">Apenas DEC</option>
              <option value="dje">Apenas DJE</option>
            </select>
          </div>
        </PageSection.Filter>

        {/* Tabela de Clientes */}
        <PageSection padding="none">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : clientes.length === 0 ? (
            <PageSection.Empty
              icon={<Building2 className="w-12 h-12" />}
              titulo="Nenhum cliente encontrado"
              descricao="Cadastre seu primeiro cliente para começar o monitoramento"
              acao={
                <button
                  onClick={() => setModalNovoAberto(true)}
                  className="px-4 py-2 rounded-lg font-medium text-[#0F1B3D]"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  Novo Cliente
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Razão Social
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      CNPJ
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Última Consulta
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">
                      DEC
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">
                      DJE
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">
                      Status
                    </th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clientes.map((cliente) => (
                    <tr
                      key={cliente.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{cliente.razao_social}</p>
                          {cliente.nome_fantasia && (
                            <p className="text-sm text-gray-500">{cliente.nome_fantasia}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {formatarCNPJ(cliente.cnpj)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatarUltimaConsulta(cliente.dec_ultima_consulta, cliente.dje_ultima_consulta)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge
                          ativo={cliente.dec_ativo}
                          ultimoErro={cliente.dec_ultimo_erro}
                          novas={cliente._count?.comunicacoes_dec || 0}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge
                          ativo={cliente.dje_ativo}
                          ultimoErro={cliente.dje_ultimo_erro}
                          novas={cliente._count?.comunicacoes_dje || 0}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            cliente.ativo
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {cliente.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/clientes/${cliente.id}`)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => navigate(`/clientes/${cliente.id}?edit=true`)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Mostrando {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-600">
                  {pagination.page} de {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
            </div>
          )}
        </PageSection>

        {/* Modal Novo Cliente */}
        {modalNovoAberto && (
          <NovoClienteModal
            onClose={() => setModalNovoAberto(false)}
            onSuccess={() => {
              setModalNovoAberto(false)
              carregarClientes()
            }}
          />
        )}
      </PageContainer>
    </AppLayout>
  )
}

